import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X, Sparkles, RotateCcw, ShoppingBag, Send, Clock, ArrowRight } from "lucide-react";
import { fetchProducts } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useConfig } from "../context/ConfigContext";
import RequestModal from "./RequestModal";
import { QuizShare } from "./QuizShare";

const QUESTIONS = [
  {
    q: "WAT VOOR AVOND WORDT HET?",
    options: [
      { label: "Gezellige borrel met vrienden of familie", score: { "red-rush": 2 } },
      { label: "Feest tot de laatste ronde", score: { "electric-energy": 2 } },
      { label: "Laat, donker en goede gesprekken", score: { "dark-drop": 2 } },
    ],
  },
  {
    q: "WELKE SMAAK SPREEKT JE AAN?",
    options: [
      { label: "Fruitig", score: { "red-rush": 3 } },
      { label: "Energiek & fris", score: { "electric-energy": 3 } },
      { label: "Drop", score: { "dark-drop": 3 } },
    ],
  },
  {
    q: "HOE STEVIG MAG HET ZIJN?",
    options: [
      { label: "Lekker licht — 10%", score: { "red-rush": 2 } },
      { label: "Midden — 12%", score: { "electric-energy": 2 } },
      { label: "Stevig — 14,5%", score: { "dark-drop": 2 } },
    ],
  },
];

const pickWinner = (answers) => {
  const totals = {};
  answers.forEach((a, i) => {
    Object.entries(QUESTIONS[i].options[a].score).forEach(([id, pts]) => {
      totals[id] = (totals[id] || 0) + pts;
    });
  });
  const tasteId = Object.keys(QUESTIONS[1].options[answers[1]].score)[0];
  const best = Math.max(...Object.values(totals));
  const top = Object.keys(totals).filter((id) => totals[id] === best);
  return top.includes(tasteId) ? tasteId : top[0];
};

export const FlavourQuiz = () => {
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [products, setProducts] = useState(null);

  const openQuiz = () => {
    setOpen(true);
    if (!products) fetchProducts().then((ps) => setProducts(ps || [])).catch(() => setProducts([]));
  };

  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (new URLSearchParams(search).get("smakenkiezer") !== "1") return;
    openQuiz();
    navigate(pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const step = answers.length;
  const winner = step === QUESTIONS.length && products ? products.find((p) => p.id === pickWinner(answers)) : null;

  return (
    <>
      {!open && (
        <button
          data-testid="flavour-quiz-tab"
          onClick={openQuiz}
          className="fixed z-[80] left-3 bottom-20 md:left-6 md:bottom-6 bg-[var(--tha-red)] text-black px-4 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2 shadow-[0_0_30px_rgba(255,30,30,0.45)] transition-transform duration-200 hover:-translate-y-0.5"
        >
          <Sparkles size={15} /> SMAKENKIEZER
        </button>
      )}
      {open && (
        <div
          data-testid="flavour-quiz-panel"
          className="fixed z-[85] left-3 right-3 bottom-20 md:right-auto md:left-6 md:bottom-6 md:w-[400px] max-h-[calc(100vh-6rem)] overflow-y-auto bg-[#0a0a0a]/95 backdrop-blur-xl border border-[var(--tha-red)] shadow-[0_0_60px_rgba(255,30,30,0.25)] rise"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--tha-border)]">
            <div className="inline-flex items-center gap-2 text-[10px] font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              <Sparkles size={12} /> SMAKENKIEZER · {Math.min(step + 1, 3)}/3
            </div>
            <button data-testid="flavour-quiz-close" onClick={() => setOpen(false)} aria-label="Sluiten" className="text-[var(--tha-muted)] hover:text-white">
              <X size={18} />
            </button>
          </div>
          <div className="h-0.5 bg-[var(--tha-border)]">
            <div className="h-full bg-[var(--tha-red)] transition-[width] duration-300" style={{ width: `${(step / 3) * 100}%` }} />
          </div>
          <div className="p-5">
            {step < QUESTIONS.length ? (
              <QuizStep key={step} index={step} onPick={(i) => setAnswers((a) => [...a, i])} />
            ) : winner ? (
              <QuizResult product={winner} onRestart={() => setAnswers([])} onClose={() => setOpen(false)} />
            ) : (
              <div className="text-sm font-mono-x text-[var(--tha-muted)] py-6">Advies laden…</div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const QuizStep = ({ index, onPick }) => (
  <div className="rise">
    <div data-testid="flavour-quiz-question" className="font-display text-2xl leading-tight">{QUESTIONS[index].q}</div>
    <div className="mt-4 space-y-2">
      {QUESTIONS[index].options.map((o, i) => (
        <button
          key={o.label}
          data-testid={`flavour-quiz-option-${index}-${i}`}
          onClick={() => onPick(i)}
          className="w-full text-left border border-[var(--tha-border)] px-4 py-3 text-sm transition-colors hover:border-[var(--tha-red)] hover:bg-[#140505] flex items-center justify-between gap-3"
        >
          {o.label} <ArrowRight size={14} className="text-[var(--tha-red)] shrink-0" />
        </button>
      ))}
    </div>
  </div>
);

const QuizResult = ({ product, onRestart, onClose }) => {
  const { addItem } = useCart();
  const { ecommerceEnabled } = useConfig();
  const [reqOpen, setReqOpen] = useState(false);
  const accent = product.accent_color || "#ff1e1e";
  const variant = (product.variants || []).find((v) => v.popular) || product.variants?.[0];

  const add = () => {
    addItem({ id: variant.id, name: `${product.name} — ${variant.label}`, price: variant.price, units: variant.units, image: product.image }, 1);
    onClose();
  };

  return (
    <div data-testid="flavour-quiz-result" className="rise">
      <div className="text-[10px] font-mono-x tracking-[0.3em]" style={{ color: accent }}>JOUW SHOT IS</div>
      <div className="mt-3 flex gap-4 items-center">
        <img src={product.image} alt={product.name} className="w-24 h-28 object-cover border border-[var(--tha-border)]" />
        <div>
          <div data-testid="flavour-quiz-result-name" className="font-display text-3xl leading-none" style={{ color: accent }}>{product.name.toUpperCase()}</div>
          <div className="mt-2 text-xs text-[var(--tha-muted)] italic">{product.tagline}</div>
          <div className="mt-2 text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">{product.alcohol} · {product.volume}</div>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {product.coming_soon ? (
          <Link to="/shop" onClick={onClose} data-testid="flavour-quiz-coming-soon" className="btn-red px-4 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2">
            <Clock size={14} /> BINNENKORT — MELD JE AAN
          </Link>
        ) : ecommerceEnabled ? (
          <button onClick={add} data-testid="flavour-quiz-add-to-cart" className="btn-red px-4 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2">
            <ShoppingBag size={14} /> IN MANDJE
          </button>
        ) : (
          <button onClick={() => setReqOpen(true)} data-testid="flavour-quiz-request" className="btn-red px-4 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2">
            <Send size={14} /> AANVRAGEN
          </button>
        )}
        <button onClick={onRestart} data-testid="flavour-quiz-restart" className="btn-ghost-red px-4 py-3 font-display tracking-wider text-sm inline-flex items-center gap-2">
          <RotateCcw size={14} /> OPNIEUW
        </button>
      </div>
      <QuizShare product={product} />
      <RequestModal open={reqOpen} onClose={() => setReqOpen(false)} product={product} variant={variant} />
    </div>
  );
};
